/**
 * Session courante.
 *
 * L'état d'authentification n'est pas stocké : il est déduit de `/api/auth/me`,
 * donc du cookie que le navigateur détient. Un onglet ouvert depuis la veille
 * découvre ainsi tout seul que sa session a expiré, au lieu d'afficher une
 * interface qui ne répond plus.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { ApiError, auth, setUnauthorizedHandler } from '@/api/client'
import type { Identity } from '@/api/types'

interface AuthState {
  user: Identity | null
  /** Vrai tant que la première vérification de session n'a pas abouti. */
  loading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Identity | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    auth
      .me()
      .then((identity) => {
        if (!cancelled) setUser(identity)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Une réponse 401 sur n'importe quel appel ramène à l'écran de connexion,
  // sans que chaque écran ait à s'en préoccuper.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
    return () => setUnauthorizedHandler(() => {})
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setUser(await auth.login(username, password))
  }, [])

  const logout = useCallback(async () => {
    try {
      await auth.logout()
    } catch (error) {
      // Une session déjà expirée côté serveur ne doit pas empêcher de sortir.
      if (!(error instanceof ApiError)) throw error
    }
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(() => ({ user, loading, login, logout }), [user, loading, login, logout])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth doit être utilisé dans un AuthProvider')
  return context
}
